import React, { useEffect, useState } from "react";
import { useAqualiteInvoiceListMutation, useAqualiteInvoiceDetailMutation } from "../../../features/tenant/Aqualite/aqualiteApi";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import toast from "react-hot-toast";

const AqualiteInvoices = () => {
  // Date range defaults (First day of current month to today)
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const defaultStartDate = firstDayOfMonth.toISOString().split("T")[0];
  const currentDate = today.toISOString().split("T")[0];

  const initialRequest = {
    invoice_no: "",
    party_code: "",
    party_name: "",
    mobile: "",
    status: "1",
    from_date: defaultStartDate,
    to_date: currentDate,
  };

  const [request, setRequest] = useState(initialRequest);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  const [myList, setMyList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // RTK Queries
  const [
    fetchInvoices,
    {
      data: invoicesResponse,
      error: invoicesError,
      isLoading: invoicesIsLoading,
    },
  ] = useAqualiteInvoiceListMutation();

  const [
    fetchInvoiceDetail,
    {
      data: detailResponse,
      error: detailError,
      isLoading: detailIsLoading,
    },
  ] = useAqualiteInvoiceDetailMutation();

  const clearFilters = () => {
    setRequest(initialRequest);
  };

  const executeSearch = () => {
    fetchInvoices({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: {
        ...request,
        limit,
        offset,
      },
    });
  };

  useEffect(() => {
    executeSearch();
  }, [limit, offset]);

  // Handle invoice list response
  useEffect(() => {
    toast.dismiss();
    if (invoicesResponse && invoicesResponse.body) {
      setMyList(invoicesResponse.body.invoices ? invoicesResponse.body.invoices : []);
      setTotalRows(invoicesResponse.body.total !== undefined ? invoicesResponse.body.total : 0);
    }
    if (invoicesError) {
      toast.error("Failed to load invoices", { id: "invoicesError" });
    }
  }, [invoicesResponse, invoicesError]);

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setLimit(newPerPage);
  };

  // Open Details Modal and fetch item specifics
  const handleViewInvoice = (row) => {
    // Resolve row invoice ID
    const invId = row.id || row.invoice_id || row.invoice_no;
    if (!invId) {
      toast.error("Invoice identifier not found");
      return;
    }
    setSelectedInvoice(row);
    setDetailModalOpen(true);

    fetchInvoiceDetail({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      invoice_id: invId,
    });
  };

  // Columns for main Invoice List DataTableComponent
  const columns = [
    {
      name: "SNo",
      cell: (row, idx) => offset + Number(idx + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "Invoice No",
      cell: (row) => row.invoice_no || row.invoiceNo || "-",
      sortable: true,
      width: "140px",
    },
    {
      name: "Invoice Date",
      cell: (row) => {
        const d = row.created_at || row.invoice_date || row.date;
        if (!d) return "-";
        return new Date(d).toLocaleDateString("en-IN");
      },
      sortable: true,
      width: "140px",
    },
    {
      name: "Party Code",
      cell: (row) => row.party_code || "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "Party Name",
      cell: (row) => row.party_name || "-",
      sortable: true,
      width: "160px",
    },
    {
      name: "Mobile",
      cell: (row) => row.mobile || "-",
      sortable: true,
      width: "130px",
    },
    {
      name: "Total Qty",
      cell: (row) => row.total_qty !== undefined ? row.total_qty : "-",
      sortable: true,
      width: "110px",
    },
    {
      name: "Total Value",
      cell: (row) => {
        const val = row.total_value || row.total_amount || row.amount;
        if (val === undefined || val === null) return "-";
        return `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
      },
      sortable: true,
      width: "130px",
    },
    {
      name: "Status",
      cell: (row) => {
        const status = String(row.status);
        if (status === "1") {
          return <span className="px-2.5 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full font-bold">Active</span>;
        } else if (status === "0") {
          return <span className="px-2.5 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full font-bold">Removed</span>;
        } else if (status === "2") {
          return <span className="px-2.5 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full font-bold">Processed</span>;
        }
        return <span className="px-2.5 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">{status || "-"}</span>;
      },
      sortable: true,
      width: "110px",
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          onClick={() => handleViewInvoice(row)}
          className="flex justify-center items-center rounded bg-primary px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:outline-none focus:ring-0 active:bg-primary-700"
        >
          View Details
        </button>
      ),
      minWidth: "120px",
    },
  ];

  // Resolve Line Items array from detail payload
  const lineItems = detailResponse?.body?.lines || detailResponse?.body?.items || detailResponse?.body?.line_items || detailResponse?.body?.data || [];

  const detailInvoice = detailResponse?.body?.invoice || {};
  const invoiceNo = detailInvoice.invoice_no || selectedInvoice?.invoice_no || "-";
  const rawDate = detailInvoice.invoice_date || detailInvoice.created_at || selectedInvoice?.created_at || selectedInvoice?.invoice_date || selectedInvoice?.date;
  const invoiceDate = rawDate ? new Date(rawDate).toLocaleDateString("en-IN") : "-";
  const statusStr = String(detailInvoice.status !== undefined ? detailInvoice.status : (selectedInvoice?.status !== undefined ? selectedInvoice.status : ""));
  const partyName = detailInvoice.party_name || selectedInvoice?.party_name || "-";
  const partyCode = detailInvoice.party_code || selectedInvoice?.party_code || "-";
  const mobile = detailInvoice.mobile || selectedInvoice?.mobile || "-";
  const totalQty = detailInvoice.total_qty !== undefined ? detailInvoice.total_qty : (selectedInvoice?.total_qty !== undefined ? selectedInvoice.total_qty : "-");
  const rawValue = detailInvoice.total_value || selectedInvoice?.total_value || detailInvoice.total_amount || selectedInvoice?.total_amount || selectedInvoice?.amount;
  const totalValue = rawValue !== undefined ? `₹${Number(rawValue).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "-";

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center min-h-screen">
        <div className="w-full">
          {/* Header */}
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Invoices List
            <LinksComponent />
          </div>

          <div className="p-4">
            <div className="w-full bg-white shadow rounded-lg overflow-hidden">

              {/* Filter Panel */}
              <div className="border-b border-gray-300 bg-gray-100 p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 items-end">

                  {/* Invoice No */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Invoice No</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Invoice No..."
                      value={request.invoice_no}
                      onChange={(e) => setRequest({ ...request, invoice_no: e.target.value })}
                    />
                  </div>

                  {/* Party Code */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Party Code</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Party Code..."
                      value={request.party_code}
                      onChange={(e) => setRequest({ ...request, party_code: e.target.value })}
                    />
                  </div>

                  {/* Party Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Party Name</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Party Name..."
                      value={request.party_name}
                      onChange={(e) => setRequest({ ...request, party_name: e.target.value })}
                    />
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Mobile</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Mobile..."
                      value={request.mobile}
                      onChange={(e) => setRequest({ ...request, mobile: e.target.value })}
                    />
                  </div>

                  {/* Status Dropdown */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      value={request.status}
                      onChange={(e) => setRequest({ ...request, status: e.target.value })}
                    >
                      <option value="">All</option>
                      <option value="1">Active</option>
                      <option value="0">Removed</option>
                      <option value="2">Processed</option>
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      value={request.from_date}
                      onChange={(e) => setRequest({ ...request, from_date: e.target.value })}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                      value={request.to_date}
                      onChange={(e) => setRequest({ ...request, to_date: e.target.value })}
                    />
                  </div>

                </div>

                {/* Filter Actions */}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium text-sm"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                  <button
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium text-sm shadow-sm"
                    onClick={executeSearch}
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* Data Table */}
              <div className="relative">
                {invoicesIsLoading && <CircleLoaderComponent />}
                <DataTableComponent
                  columns={columns}
                  data={myList}
                  totalRows={totalRows}
                  handlePerRowsChange={handlePerRowsChange}
                  handlePageChange={handlePageChange}
                />
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Details Popup Modal */}
      {detailModalOpen && (
        <ModalComponent
          show={detailModalOpen}
          setShow={setDetailModalOpen}
          size="5xl"
        >
          <div className="p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="text-xl font-bold text-gray-800">Invoice Details</h3>
            </div>

            {detailIsLoading ? (
              <div className="py-20">
                <CircleLoaderComponent />
              </div>
            ) : detailError ? (
              <div className="py-8 text-center text-red-600 font-semibold">
                Failed to load invoice details. Please try again.
              </div>
            ) : (
              <div>
                {/* Details Summary Fields */}
                <div className="bg-gray-50 border rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Invoice No</span>
                    <span className="text-sm font-bold text-gray-800">{invoiceNo}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Invoice Date</span>
                    <span className="text-sm font-semibold text-gray-800">{invoiceDate}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Status</span>
                    <span className="text-sm font-semibold">
                      {statusStr === "1" ? (
                        <span className="text-green-600 font-bold">Active</span>
                      ) : statusStr === "0" ? (
                        <span className="text-red-600 font-bold">Removed</span>
                      ) : statusStr === "2" ? (
                        <span className="text-blue-600 font-bold">Processed</span>
                      ) : (
                        <span className="text-gray-500 font-bold">{statusStr || "-"}</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Party Name</span>
                    <span className="text-sm font-semibold text-gray-800">{partyName}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Party Code</span>
                    <span className="text-sm font-semibold text-gray-800">{partyCode}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Mobile</span>
                    <span className="text-sm font-semibold text-gray-800">{mobile}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Total Qty</span>
                    <span className="text-sm font-semibold text-gray-800">{totalQty}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase">Total Value</span>
                    <span className="text-sm font-bold text-gray-800">{totalValue}</span>
                  </div>
                </div>

                {/* Line Items Table */}
                <h4 className="text-lg font-bold text-gray-800 mb-3">Line Items</h4>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">SNo</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">SKU Code</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">QR String</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Scan Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lineItems.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">
                            No items found in this invoice.
                          </td>
                        </tr>
                      ) : (
                        lineItems.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3.5 text-sm text-gray-700">{idx + 1}</td>
                            <td className="px-4 py-3.5 text-sm text-gray-900 font-medium">
                              {item.sku_code || "-"}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-gray-700 break-all">
                              {item.qr_string || "-"}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-center">
                              {String(item.status) === "1" ? (
                                <span className="px-2 py-0.5 text-xs font-semibold text-green-800 bg-green-100 rounded-full font-bold">Active</span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full font-bold">Pending</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-gray-700">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString("en-IN") : "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end mt-6 pt-3 border-t">
                  <button
                    onClick={() => setDetailModalOpen(false)}
                    className="px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all font-medium text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default AqualiteInvoices;
